import { DropDown, Form, Modal, Pending } from "@finbeat/react-toolkit";
import { AxiosError } from "axios";
import { changeBlocks, getBlocks } from "axios/clientApi";
import { getSelectList } from "axios/dictionaryApi";
import useApi from "hooks/useApi";
import { AxiosErrorResponse } from "models/api/axiosErrorResponse";
import { ClientBlockAdd, ClientBlockReasonAdd } from "models/api/client";
import Id from "models/id";
import { useMemo, useRef, useState } from "react";
import { Trans } from "react-i18next";
import { useMutation, useQuery } from "react-query";
import { Button, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import i18n from "utils/i18n";
import { useClientWorkplaceState } from "../Clients";
import ClientBlocksForm from "./ClientBlocksForm";
import './ClientBlocksModal.scss';

type ClientBlocksModalProps = Id & {
    isOpen?: boolean;
    toggle: (item: string) => void;
};

export type ClientBlocksFormType = Id & {
    clientBlocks: ClientBlockAdd[];
};

const ClientBlocksModal = ({ id: clientId, isOpen = false, toggle }: ClientBlocksModalProps) => {
    const api = useApi();
    const { handleError, fetchQuery } = useClientWorkplaceState();

    const [blocks, setBlocks] = useState<ClientBlockAdd[]>([]);
    const [actions, setActions] = useState<DropDown[]>([]);
    const [periods, setPeriods] = useState<DropDown[]>([]);
    const [reasons, setReasons] = useState<DropDown[]>([]);

    const onError = (error: AxiosError<AxiosErrorResponse>) => {
        handleError(error.response?.data);
        toggle('');
    };

    const { isFetching: blocksIsFetching } = useQuery(
        'clientBlocks' + clientId,
        () => getBlocks(api, clientId ?? 0),
        {
            onSuccess: (clientBlocks) => setBlocks(clientBlocks),
            onError: onError,
            enabled: isOpen,
        }
    );

    const { isFetching: dictsIsFetching } = useQuery(
        ['clientBlocksDicts', i18n.language],
        () => getSelectList(api,
            [
                'ClientBlockActions',
                'ClientBlockPeriods',
                'ClientBlockReasons'
            ]),
        {
            onSuccess: (data) => {
                if (data) {
                    setActions(data.ClientBlockActions ?? []);
                    setPeriods(data.ClientBlockPeriods ?? []);
                    setReasons(data.ClientBlockReasons ?? []);
                }
            },
            onError: onError,
            enabled: isOpen,
        }
    );

    const defaultValues = useMemo(
        () => ({
            clientId: clientId,
            clientBlocks: blocks
        }),
        [blocks]
    );

    const changeBlocksdMutation = useMutation((formattedItems: ClientBlockAdd[]) =>
        changeBlocks(api, clientId ?? '', formattedItems),
        {
            onSuccess: () => {
                fetchQuery.refetch();
            },
        });

    const saveChanges = (data: ClientBlocksFormType) => {
        const formattedItems: ClientBlockAdd[] = [];
        data.clientBlocks.forEach((b) => {
            b.reasons = b.reasons
                // remove empty values
                .filter((v) => v.name)
                // remove duplicates
                .reduce((result, item) => {
                    if (!result.find((i) => i.name === item.name)) {
                        result.push(item);
                    }

                    return result;
                }, [] as ClientBlockReasonAdd[]);

            formattedItems.push({
                id: b.id,
                action: b.action,
                adminComment: b.adminComment,
                userComment: b.userComment,
                period: b.period,
                reasons: b.reasons,
            });
        });

        toggle('');

        if (clientId) {
            changeBlocksdMutation.mutate(formattedItems);
            fetchQuery.refetch();
        }
    };

    const formRef = useRef<HTMLFormElement>(null);

    return (
        <Modal size="lg" isOpen={isOpen} toggle={() => toggle('')} scrollable>
            <ModalHeader toggle={() => toggle('')}>
                <Trans i18nKey="BackOfficeClientBlocks" /> #{clientId}
            </ModalHeader>
            <Form<ClientBlocksFormType>
                onSubmit={(data) => saveChanges(data)}
                defaultValues={defaultValues}
                className="form-in-modal"
                clearAfterSubmit={false}
                formProps={{ mode: 'all' }}
                innerRef={formRef}
            >
                <ModalBody className="custom-scrollbar">
                    {(blocksIsFetching || dictsIsFetching) && <Pending />}
                    {!(blocksIsFetching || dictsIsFetching) && (
                        <ClientBlocksForm
                            actions={actions}
                            periods={periods}
                            reasons={reasons}
                        />
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => toggle('')}>
                        <Trans i18nKey="BackOfficeCancel" />
                    </Button>
                    <Button color="success" disabled={blocksIsFetching || dictsIsFetching}>
                        <Trans i18nKey="BackOfficeSave" />
                    </Button>
                </ModalFooter>
            </Form>
        </Modal>
    );
}

export default ClientBlocksModal;
